import { useEffect, useRef, useState } from 'react';
import { endHelp } from '../data/supabase';
import { NavLink, Outlet, useLocation } from 'react-router';
import { backend, demoMode, openVirtualScreen, startSync, useApp, useTheme, useUiStyle, type Theme, type UiStyle } from '../data/store';
import { Icon } from '../ui/icons';
import { AskHost, ConfirmHost, IconButton, Popover, Segmented, Toasts } from '../ui/controls';
import { openSheet, useNav } from './ui';
import { SheetHost } from '../sheets/SheetHost';
import { Boundary } from './Errors';
import { usePresenceWatch } from './presence';
import { NoAccess, Onboarding } from './Onboarding';
import { unlockedOr } from './lock';
import { setSheetGuard } from './ui';

export function Shell() {
  const loc = useLocation();
  const studio = loc.pathname.startsWith('/studio');
  const ws = useApp((s) => s.ws);
  const error = useApp((s) => s.error);
  const needsMasjid = useApp((s) => s.needsMasjid);
  // A NoorClass account with no masjid: NoorView stays locked behind the "no access" message.
  const noAccess = useApp((s) => s.needsMasjid && !!s.session?.noorclassOnly);
  useEffect(() => { startSync(); setSheetGuard((open) => void unlockedOr(open)); return () => setSheetGuard(null); }, []);
  usePresenceWatch();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); openSheet({ name: 'search' }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <div className="ambient" aria-hidden><i /><i /><i /></div>
      <div className={`app${studio ? ' collapsed' : ''}`} inert={noAccess} aria-hidden={noAccess || undefined}>
        <Sidebar collapsed={studio} />
        <main className="main" id="main">
          <HelpBar />
          {noAccess ? <div className="loading-page" aria-hidden /> : needsMasjid ? <Onboarding /> : error && !ws ? (
            <div className="empty" style={{ flex: 1 }}><b>Couldn’t load your masjid</b><span>{error}</span><button type="button" className="btn primary" onClick={() => useApp.getState().reload()}>Try again</button></div>
          ) : !ws ? (
            <div className="loading-page" aria-busy="true"><div className="skeleton" style={{ height: 38, width: 380 }} /><div className="skeleton" style={{ flex: 1 }} /></div>
          ) : (
            <Boundary label="This page"><Outlet /></Boundary>
          )}
        </main>
      </div>
      {noAccess && <NoAccess />}
      <SheetHost />
      <ConfirmHost />
      <AskHost />
      <Toasts />
    </>
  );
}

/** Glass or Simple (flat). Sits just above the colour themes wherever they appear. */
function UiSwitch() {
  const ui = useUiStyle();
  return <Segmented label="Interface style" value={ui.ui} onChange={(u: UiStyle) => ui.set(u)} options={[{ value: 'glass', label: 'Glass' }, { value: 'simple', label: 'Simple' }]} />;
}

/**
 * The appearance switch (Glass or Simple; Legacy, Light or Dark), behind one control so the sidebar stays calm: a
 * row showing the current choice in the full sidebar, an icon in the Studio's narrow one.
 */
function ThemeButton({ wide }: { wide?: boolean }) {
  const theme = useTheme();
  const ui = useUiStyle();
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const icon = theme.theme === 'dark' ? 'moon' : 'sun';
  const name = (s: string) => s[0]!.toUpperCase() + s.slice(1);
  return (
    <>
      {wide
        ? <button ref={ref} type="button" className="side-appear" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="dialog">
            <span className="sa-ic"><Icon name={icon} size={18} /></span>
            <span className="sa-t"><b>Appearance</b><small>{name(ui.ui)} · {name(theme.theme)}</small></span>
            <Icon name="chevronsUpDown" size={16} />
          </button>
        : <IconButton ref={ref} icon={icon} label="Appearance" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="side-theme" />}
      <Popover anchor={ref.current} open={open} onClose={() => setOpen(false)} width={300} align="start" label="Appearance">
        <div className="stack">
          <div className="section-label">Style</div>
          <UiSwitch />
          <div className="section-label">Appearance</div>
          <Segmented label="Appearance" value={theme.theme} onChange={(t: Theme) => theme.set(t)}
            options={[{ value: 'legacy', label: 'Legacy' }, { value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]} />
        </div>
      </Popover>
    </>
  );
}

/**
 * One page in the sidebar. Moving between pages (and the sidebar folding for the Studio) animates its parts
 * separately: the blue highlight slides to the new page, each icon glides to its new place, and the words fade, so
 * nothing is squashed or jumps (see the view transitions in app.css).
 */
function NavItem({ to, end, icon, label, vt, on }: { to: string; end?: boolean; icon: string; label: string; vt: string; on: boolean }) {
  return (
    <NavLink viewTransition to={to} end={end} className={on ? 'on' : ''} title={label} aria-current={on ? 'page' : undefined}>
      {on && <i className="nav-pill" aria-hidden style={{ viewTransitionName: 'nv-nav-pill' }} />}
      <span className="nav-ico" style={{ viewTransitionName: `nv-ico-${vt}` }}><Icon name={icon} /></span>
      <span className="nav-label vt-fade" style={{ viewTransitionName: `nv-lbl-${vt}` }}>{label}</span>
    </NavLink>
  );
}

function Sidebar({ collapsed }: { collapsed: boolean }) {
  const loc = useLocation();
  const session = useApp((s) => s.session);
  const ws = useApp((s) => s.ws);
  const nav = useNav();
  const moreRef = useRef<HTMLButtonElement>(null);
  const [menu, setMenu] = useState(false);
  const initials = (session?.name ?? 'You').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const role = demoMode ? 'Demo' : session?.role === 'owner' ? 'Super Admin' : session?.role === 'admin' ? 'Admin' : 'Editor';
  return (
    <aside className="sidebar glass" aria-label="Main">
      <NavLink viewTransition to="/" className="brand" aria-label="NoorView home"><img src="/noorview-logo.png" alt="" style={{ viewTransitionName: 'nv-brand-logo' }} /><span className="vt-fade" style={{ viewTransitionName: 'nv-brand-text' }}><b>Noor</b>View</span></NavLink>
      <nav className="nav">
        <NavItem to="/" end icon="grid" label="Dashboard" vt="dash" on={loc.pathname === '/'} />
        <NavItem to="/screens" icon="tv" label="Screens" vt="screens" on={loc.pathname.startsWith('/screens')} />
        {/* The menu's Studio is the blank canvas; a screen's own design opens from Screens › Edit in Studio */}
        <NavItem to="/studio" icon="studio" label="Studio" vt="studio" on={loc.pathname.startsWith('/studio')} />
        <NavItem to="/settings" icon="gear" label="Settings" vt="settings" on={loc.pathname.startsWith('/settings')} />
      </nav>
      <div className="spacer" />
      {!collapsed && (
        <>
          <ThemeButton wide />
          {/* One small card: who makes NoorView, and the plan (with Upgrade until the masjid is on Pro). */}
          <div className="side-card glass">
            <a className="sc-maker" href="https://www.noormsystems.co.uk" target="_blank" rel="noopener noreferrer" aria-label="NoorView is a product of Noor MS. Visit the Noor MS website (opens in a new tab)">
              <img src="/noorms-logo.png" alt="" width={28} height={30} />
              <span><small>A product of</small><b>Noor MS</b></span>
              <Icon name="external" size={16} />
            </a>
            {ws && (ws.masjid.plan === 'pro'
              ? <div className="sc-plan"><span>NoorView Pro</span><Icon name="check" size={16} /></div>
              : <button type="button" className="sc-plan sc-up" onClick={() => openSheet({ name: 'upgrade' })}>
                  <span><b>Unlock Pro</b><small>{ws.masjid.plan === 'basic' ? 'Basic plan' : 'Free plan'}</small></span>
                  <span className="sc-btn">Upgrade</span>
                </button>)}
          </div>
        </>
      )}
      {collapsed && <ThemeButton />}
      <div className="profile">
        <button type="button" className="avatar" style={{ viewTransitionName: 'nv-avatar' }} aria-label={`${session?.name ?? 'Account'} menu`} onClick={() => (collapsed ? setMenu(true) : nav('/settings/account'))} ref={collapsed ? moreRef : undefined}>{initials}</button>
        {!collapsed && <div className="who"><b>{session?.name?.split(/\s+/)[0]}</b><small>{role}</small></div>}
        {!collapsed && <><div className="spacer" /><IconButton ref={moreRef} icon="more" label="Account options" size="sm" onClick={() => setMenu((m) => !m)} /></>}
      </div>
      <Popover anchor={moreRef.current} open={menu} onClose={() => setMenu(false)} width={264} align="start" label="Account">
        <div className="menu">
          <button type="button" onClick={() => { setMenu(false); nav('/settings/account'); }}><Icon name="users" size={18} />Account and team</button>
          <button type="button" onClick={() => { setMenu(false); nav('/settings/support'); }}><Icon name="phone" size={18} />Support Centre</button>
          {backend.kind === 'sandbox' && <button type="button" onClick={() => { setMenu(false); openVirtualScreen(); }}><Icon name="tv" size={18} />Open a virtual screen</button>}
          <button type="button" className="danger" onClick={async () => { setMenu(false); await backend.signOut(); useApp.getState().setSession(null); nav('/login'); }}><Icon name="logout" size={18} />Sign out</button>
        </div>
      </Popover>
    </aside>
  );
}

/** Noor MS in a masjid's NoorView with remote access: who you are, when access ends, and a way out. */
function HelpBar() {
  const helping = useApp((s) => s.session?.helping);
  const name = useApp((s) => s.ws?.masjid.name);
  const tz = useApp((s) => s.ws?.masjid.timezone ?? 'Europe/London');
  const [ended, setEnded] = useState(false);
  // The database stops Noor MS the moment the masjid switches remote access off (or it runs out); this closes the
  // NoorView too, within a few seconds, so nothing on screen suggests access is still there.
  useEffect(() => {
    if (!helping) return;
    let stop = false;
    const check = async () => {
      if (stop) return;
      const h = Date.parse(helping.until) > Date.now() ? await backend.support.help().catch(() => helping.until) : null;
      if (!h && !stop) { stop = true; setEnded(true); endHelp(); await backend.signOut().catch(() => {}); }
    };
    const t = setInterval(check, 10_000);
    const onFocus = () => { if (document.visibilityState === 'visible') void check(); };
    document.addEventListener('visibilitychange', onFocus); window.addEventListener('focus', onFocus);
    return () => { stop = true; clearInterval(t); document.removeEventListener('visibilitychange', onFocus); window.removeEventListener('focus', onFocus); };
  }, [helping]);
  if (ended) return (
    <div className="help-ended" role="alertdialog" aria-modal="true" aria-labelledby="help-ended-t">
      <div className="card glass">
        <span className="help-ended-ic"><Icon name="lock" size={28} /></span>
        <h2 id="help-ended-t">Remote access has ended</h2>
        <p>{name ?? 'The masjid'} switched off remote access, or the time ran out. You’ve been signed out of their NoorView.</p>
        <button type="button" className="btn primary" onClick={() => { location.href = '/login'; }}>OK</button>
      </div>
    </div>
  );
  if (!helping) return null;
  const until = new Date(helping.until).toLocaleTimeString('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit' });
  return (
    <div className="help-bar" role="status">
      <Icon name="users" size={16} /><span>Remote access to <b>{name ?? 'this masjid'}</b> as Noor MS · ends at {until}. Everything you change is live.</span>
      <button type="button" className="btn sm plain" onClick={async () => { endHelp(); await backend.signOut(); location.href = '/login'; }}>Leave</button>
    </div>
  );
}
